import { FC } from "react";
import { Box, Text } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { Order } from "../../types/interfaces";
import { OrderStatus, RemovableItemOrderType } from "../../types/enums";
import { getRdv2PreviousProducts } from "../../utils/product.utils";
import { Banner } from "../Banner";

type Rdv2ContinueBannerProps = {
  order: Order;
  onSubmit: () => Promise<void>;
};

const getOrderedAppointmentLabel = (order: Order) => {
  const orderTypes = Object.values(getRdv2PreviousProducts(order)).map(
    product =>
      product?.removableItem?.[0]?.fullDentureOrderType ??
      product?.removableItem?.[0]?.partialDentureOrderType
  );
  const hasTryIn = orderTypes.includes(RemovableItemOrderType.TRY_IN);
  const hasWaxBite = orderTypes.includes(RemovableItemOrderType.WAX_BITE);

  if (hasTryIn && hasWaxBite) {
    return "un essayage et une cire d'occlusion";
  }
  return hasTryIn ? "un essayage" : "une cire d'occlusion";
};

export const Rdv2ContinueBanner: FC<Rdv2ContinueBannerProps> = ({
  order,
  onSubmit,
}) => {
  const navigate = useNavigate();

  const handleContinue = async () => {
    const previousProducts = getRdv2PreviousProducts(order);
    await onSubmit();
    navigate("/create-new-order", {
      state: {
        id: order.id,
        workflow: Object.keys(previousProducts),
        cameFromOtherProduct: true,
        isRdv2Continuation: true,
        previousProducts,
      },
    });
  };

  return (
    <Banner
      content={
        <Box>
          <Text size="md" fw={600} style={{ color: "white" }}>
            {`Vous avez récemment commandé ${getOrderedAppointmentLabel(order)}.`}
          </Text>
          <Text size="md" fw={600} style={{ color: "white" }}>
            Poursuivez votre commande ici.
          </Text>
        </Box>
      }
      actionButton={{ onClick: handleContinue, label: "Poursuivre" }}
    />
  );
};

export const shouldShowRdv2ContinueBanner = (order: Order): boolean =>
  !!order &&
  order.status === OrderStatus.DELIVERED &&
  Object.keys(getRdv2PreviousProducts(order)).length > 0;
